import {
    Autocomplete, Box, Button, CircularProgress, Dialog, IconButton,
    TextField, Tooltip, Typography
} from "@mui/material"
import { AddOutlined, CloseOutlined, DeleteOutlineOutlined, EditOutlined } from "@mui/icons-material"
import { FC, useEffect, useState } from "react"
import { LoadingButton } from "@mui/lab"
import { Apis } from "../../helpers"

export interface BankAccountItem {
    _id: string
    bankId: string
    accountNumber: string
    bank_name: string
}
interface BankOption {
    _id: string
    bank_name: string
    bank_code: string
}

interface Props {
    selectedAccountId?: string
    onSelect: (account: BankAccountItem) => void
    close: () => void
}
interface StateProps {
    accounts: BankAccountItem[]
    loading: boolean
    banks: BankOption[]
    editing: BankAccountItem | null
    showForm: boolean
    formBank: BankOption | null
    formAccountNumber: string
    saving: boolean
    error: string | null
}

const SelectBankAccountModal: FC<Props> = ({ selectedAccountId, onSelect, close }) => {
    const [state, setState] = useState<StateProps>({
        accounts: [], loading: true, banks: [], editing: null,
        showForm: false, formBank: null, formAccountNumber: '', saving: false, error: null
    })
    const { accounts, loading, banks, editing, showForm, formBank, formAccountNumber, saving, error } = state

    const fetchAccounts = () => {
        setState(s => ({ ...s, loading: true }))
        Apis({ module: 'Users', action: 'getBankAccounts' })
            .then((resp: any) => setState(s => ({ ...s, accounts: resp || [], loading: false })))
            .catch(() => setState(s => ({ ...s, loading: false })))
    }

    useEffect(() => {
        fetchAccounts()
        Apis({ module: 'Banks', action: 'getBanks' })
            .then((resp: any) => setState(s => ({ ...s, banks: resp || [] })))
            .catch(() => { })
        // eslint-disable-next-line
    }, [])

    const openAdd = () => setState(s => ({ ...s, showForm: true, editing: null, formBank: null, formAccountNumber: '', error: null }))
    const openEdit = (account: BankAccountItem) => setState(s => ({
        ...s, showForm: true, editing: account, formAccountNumber: account.accountNumber,
        formBank: banks.find(b => b._id === account.bankId) || null, error: null
    }))

    const saveForm = () => {
        if (!formBank || !formAccountNumber.trim()) {
            setState(s => ({ ...s, error: 'Please select a bank and enter the account number.' }))
            return
        }
        setState(s => ({ ...s, saving: true, error: null }))
        Apis({
            module: 'Users',
            action: editing ? 'updateBankAccount' : 'addBankAccount',
            bankAccountId: editing?._id,
            bankId: formBank._id,
            accountNumber: formAccountNumber.trim()
        }).then(() => {
            setState(s => ({ ...s, saving: false, showForm: false }))
            fetchAccounts()
        }).catch(() => setState(s => ({ ...s, saving: false, error: 'Something went wrong. Please try again.' })))
    }

    const deleteAccount = (account: BankAccountItem) => {
        Apis({ module: 'Users', action: 'deleteBankAccount', bankAccountId: account._id })
            .then(() => setState(s => ({ ...s, accounts: s.accounts.filter(a => a._id !== account._id) })))
            .catch(() => { })
    }

    return (
        <Dialog open onClose={close} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography sx={{ fontSize: 16, fontWeight: 800 }}>
                        {showForm ? (editing ? 'Edit Bank Account' : 'Add Bank Account') : 'Select Bank Account'}
                    </Typography>
                    <IconButton size="small" onClick={close}>
                        <CloseOutlined sx={{ fontSize: 18 }} />
                    </IconButton>
                </Box>

                {showForm ? (
                    <Box>
                        <Autocomplete
                            options={banks}
                            getOptionLabel={(o) => o.bank_name}
                            isOptionEqualToValue={(o, v) => o._id === v._id}
                            value={formBank}
                            onChange={(_, value) => setState(s => ({ ...s, formBank: value, error: null }))}
                            renderInput={(params) => <TextField {...params} label="Bank" size="small" />}
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            fullWidth
                            size="small"
                            label="Account Number"
                            value={formAccountNumber}
                            onChange={e => setState(s => ({ ...s, formAccountNumber: e.target.value, error: null }))}
                            error={Boolean(error)}
                            helperText={error}
                            sx={{ mb: 2.5 }}
                        />
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                fullWidth
                                variant="outlined"
                                color="inherit"
                                disabled={saving}
                                onClick={() => setState(s => ({ ...s, showForm: false }))}
                                sx={{ textTransform: 'none' }}
                            >
                                Cancel
                            </Button>
                            <LoadingButton
                                fullWidth
                                variant="contained"
                                loading={saving}
                                onClick={saveForm}
                                sx={{ textTransform: 'none' }}
                            >
                                Save
                            </LoadingButton>
                        </Box>
                    </Box>
                ) : (
                    <Box>
                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
                        ) : !accounts.length ? (
                            <Typography sx={{ fontSize: 13, color: 'text.secondary', textAlign: 'center', py: 3 }}>
                                No bank accounts added yet.
                            </Typography>
                        ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                                {accounts.map(account => (
                                    <Box
                                        key={account._id}
                                        onClick={() => onSelect(account)}
                                        sx={{
                                            display: 'flex', alignItems: 'center', gap: 1, p: 1.4, borderRadius: 2, cursor: 'pointer',
                                            border: '1.5px solid', borderColor: selectedAccountId === account._id ? '#8c7c0c' : '#e2e8f0',
                                            bgcolor: selectedAccountId === account._id ? 'rgba(140,124,12,.06)' : '#fff'
                                        }}
                                    >
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography sx={{ fontSize: 13.5, fontWeight: 700 }} noWrap>{account.bank_name}</Typography>
                                            <Typography sx={{ fontSize: 12, color: 'text.secondary' }} noWrap>{account.accountNumber}</Typography>
                                        </Box>
                                        <Tooltip title="Edit">
                                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); openEdit(account) }}>
                                                <EditOutlined sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete">
                                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); deleteAccount(account) }}>
                                                <DeleteOutlineOutlined sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                ))}
                            </Box>
                        )}
                        <Button
                            fullWidth
                            variant="outlined"
                            startIcon={<AddOutlined />}
                            onClick={openAdd}
                            sx={{ textTransform: 'none' }}
                        >
                            Add Bank Account
                        </Button>
                    </Box>
                )}
            </Box>
        </Dialog>
    )
}

export default SelectBankAccountModal
